import { svgNameSpaceURI } from './svgNamespace.js'
import { roundDecimalPlaces, ensureIsArray } from './utils.js'


const addUse = function(plotElement, ref, attributeMap) {
    let element = document.createElementNS(svgNameSpaceURI, `use`)
    let useId = `${ref}-use`
    element.setAttribute(`href`, `#${ref}`)
    element.setAttribute(`id`, useId)
    for (let [attribute, value] of attributeMap.entries()) {
        element.setAttribute(attribute, value)
    }
    plotElement.appendChild(element)
    return [useId, element]
}


const drawPath = function(plotElement, pathD, attributeMap, id) {
    let element = document.createElementNS(svgNameSpaceURI, `path`)
    element.setAttribute(`id`, `${id}`)
    element.setAttribute(`d`, pathD)
    for (let [attribute, value] of attributeMap.entries()) {
        element.setAttribute(attribute, value)
    }
    plotElement.appendChild(element)
    return element
}


const drawCircle = function(plotElement, cx, cy, r, attributeMap, id) {
    let element = document.createElementNS(svgNameSpaceURI, `circle`)
    element.setAttribute(`id`, `${id}`)
    element.setAttribute(`cx`, cx)
    element.setAttribute(`cy`, cy)
    element.setAttribute(`r`, r)
    for (let [attribute, value] of attributeMap.entries()) {
        element.setAttribute(attribute, value)
    }
    plotElement.appendChild(element)
    return element
}


const createMorphologyElem = function(in1, operator, changeRadius, resultName) {
    let feMorphology = document.createElementNS(svgNameSpaceURI, 'feMorphology')
    feMorphology.setAttribute('in', in1)
    feMorphology.setAttribute('operator', operator)
    feMorphology.setAttribute('radius', changeRadius)
    feMorphology.setAttribute('result', resultName)
    return feMorphology
}


const createFloodElem = function(floodColor, floodOpacity, resultName) {
    let feFlood = document.createElementNS(svgNameSpaceURI, 'feFlood')
    feFlood.setAttribute('flood-color', floodColor)
    feFlood.setAttribute('flood-opacity', floodOpacity)
    feFlood.setAttribute('result', resultName)
    return feFlood
}


const createBlurElem = function(in1, stdDev, resultName) {
    let feGaussianBlur = document.createElementNS(svgNameSpaceURI, 'feGaussianBlur')
    feGaussianBlur.setAttribute('in', in1)
    feGaussianBlur.setAttribute('stdDeviation', stdDev)
    feGaussianBlur.setAttribute('result', resultName)
    return feGaussianBlur
}


const createDisplacementMap = function(in1, in2, resultName, scale=5, noiseType='turbulence') {
    let feDisplacementMap = document.createElementNS(svgNameSpaceURI, 'feDisplacementMap')
    feDisplacementMap.setAttribute('in', in1)
    feDisplacementMap.setAttribute('in2', in2)
    feDisplacementMap.setAttribute('scale', scale)
    feDisplacementMap.setAttribute('result', resultName)
    // You should not need to modify these; feTurbulence for 
    // the RGB channels should be variable in all directions
    feDisplacementMap.setAttribute('xChannelSelector', 'R')
    feDisplacementMap.setAttribute('yChannelSelector', 'B')
    return feDisplacementMap
}


const createTurbulence = function(resultName, baseFreq='2 3.5', octaves=5, type='turbulence') {
    let turbulence =  document.createElementNS(svgNameSpaceURI, "feTurbulence")
    turbulence.setAttribute('type', type)
    turbulence.setAttribute('baseFrequency', baseFreq)
    turbulence.setAttribute('numOctaves', octaves)
    turbulence.setAttribute('result', resultName)
    turbulence.setAttribute('seed', Math.round(Math.random() * 5000))
    return turbulence
}


const createSpecularLight = function(inputName, resultName, constant, exponent, surfaceScale, lightColor) {
    let specularLight =  document.createElementNS(svgNameSpaceURI, "feSpecularLighting")
    specularLight.setAttribute('in', inputName)
    specularLight.setAttribute('result', resultName)
    specularLight.setAttribute('specularConstant', constant)
    specularLight.setAttribute('specularExponent', exponent)
    specularLight.setAttribute('surfaceScale', surfaceScale)
    specularLight.setAttribute('lighting-color', lightColor)
    return specularLight
}


const createDiffuseLight = function(inputName, resultName, constant, surfaceScale, lightColor) {
    let diffuseLight =  document.createElementNS(svgNameSpaceURI, "feDiffuseLighting")
    diffuseLight.setAttribute('in', inputName)
    diffuseLight.setAttribute('result', resultName)
    diffuseLight.setAttribute('diffuseConstant', constant)
    diffuseLight.setAttribute('surfaceScale', surfaceScale)
    diffuseLight.setAttribute('lighting-color', lightColor)
    return diffuseLight
}


const addPointLight = function(lightingElement, x, y, z) {
    let pointLight =  document.createElementNS(svgNameSpaceURI, "fePointLight")
    pointLight.setAttribute('x', x)
    pointLight.setAttribute('y', y)
    pointLight.setAttribute('z', z)
    lightingElement.appendChild(pointLight)
}


const addDistantLight = function(lightingElement, azimuth, elevation) {
    let pointLight =  document.createElementNS(svgNameSpaceURI, "feDistantLight")
    pointLight.setAttribute('azimuth', azimuth)
    pointLight.setAttribute('elevation', elevation)
    lightingElement.appendChild(pointLight)
}


const addSpotLight = function(lightingElement, x, y, z, coneAngle) {
    let pointLight =  document.createElementNS(svgNameSpaceURI, "feSpotLight")
    pointLight.setAttribute('x', x)
    pointLight.setAttribute('y', y)
    pointLight.setAttribute('z', z)
    pointLight.setAttribute('limitingConeAngle', coneAngle)
    lightingElement.appendChild(pointLight)
}




const createCompositeElem = function(operator, in1, in2, resultName) {
    let feComposite = document.createElementNS(svgNameSpaceURI, 'feComposite')
    feComposite.setAttribute('operator', operator)
    feComposite.setAttribute('in', in1)
    feComposite.setAttribute('in2', in2)
    feComposite.setAttribute('result', resultName)
    return feComposite
}


const createCompositeLight = function(operator, in1, in2, resultName) {
    let feComposite = document.createElementNS(svgNameSpaceURI, 'feComposite')
    feComposite.setAttribute('operator', operator)
    feComposite.setAttribute('in', in1)
    feComposite.setAttribute('in2', in2)
    feComposite.setAttribute('result', resultName)
    feComposite.setAttribute('k1', 0)
    feComposite.setAttribute('k2', 1)
    feComposite.setAttribute('k3', 1)
    feComposite.setAttribute('k4', 0)
    return feComposite
}




const createBlendElem = function(mode, in1, in2, resultName) {
    let feBlend = document.createElementNS(svgNameSpaceURI, 'feBlend')
    feBlend.setAttribute('mode', mode)
    feBlend.setAttribute('in', in1)
    feBlend.setAttribute('in2', in2)
    feBlend.setAttribute('result', resultName)
    return feBlend
}


// TODO: make the R/G/B values configurable. 
//       Maybe using different combos there would give better 
//       randomization results (and reduce holes)
const createColorMatrixAlpha = function(input, resultName, intensityVector) {
    let feColorMatrix = document.createElementNS(svgNameSpaceURI, 'feColorMatrix')
    feColorMatrix.setAttribute('in', input)
    feColorMatrix.setAttribute('result', resultName)
    feColorMatrix.setAttribute('values', `
                            1 0 0 0 0
                            0 1 0 0 0 
                            0 0 1 0 0
                            ${intensityVector} 0
                            `)
    return feColorMatrix
}


// Allow creation of the full color matrix,
// you need to specify all values
const createFullColorMatrix = function(input, resultName, colorMat) {
    let feColorMatrix = document.createElementNS(svgNameSpaceURI, 'feColorMatrix')
    feColorMatrix.setAttribute('in', input)
    feColorMatrix.setAttribute('result', resultName)
    feColorMatrix.setAttribute('values', `
                            ${colorMat}
                            `)
    return feColorMatrix
}




// Very useful to do transforms. 
// The origin allows scaling from locations other than the 
// regular origin
const transformScale = function(elements, origin, scaleNums) {
    for (let element of elements) {
        element.setAttribute('transform-origin', origin)
        element.setAttribute('transform', `scale(${scaleNums})`)
    }
}


const transformScaleSkewY = function(elements, origin, scaleNums, skewX, skewY, rotate) {
    for (let element of elements) {
        let transformStr = ``
        if (origin) {
            element.setAttribute('transform-origin', origin)
        }
        if (scaleNums) {
            transformStr += `scale(${scaleNums}) `
        }
        if (skewY) {
            transformStr += `skewY(${skewY}) `
        }
        if (skewX) {
            transformStr += `skewX(${skewX}) `
        }
        if (rotate) {
            transformStr += `rotate(${rotate}) `
        }

        element.setAttribute('transform', transformStr)
    }
}




// Give the filter a wide range so I don't get 
// edge effects
const setFilterRange = function(filterElement) {
    filterElement.setAttribute('x', '-500%')
    filterElement.setAttribute('y', '-500%')
    filterElement.setAttribute('width', '1000%')
    filterElement.setAttribute('height', '1000%')
}


// Uses turbulence to displace an input
const createTurbulenceDisplacement = function(freq, octaves, displacement=1, inputName='SourceGraphic', noiseType='turbulence') {
    let filters = []
    filters.push(createTurbulence('turbulence-displace-1', freq, octaves, noiseType))
    filters.push(createDisplacementMap(inputName, 'turbulence-displace-1', 'turbulence-displace-result', displacement))
    return filters
}


// Uses turbulence to displace an input
const createSpecularPointLight = function(resultName, constant, exponent, surfaceScale, lightColor, x, y, z, inputName='SourceGraphic', composite='SourceGraphic') {
    let filters = []
    filters.push(createSpecularLight(inputName, resultName, constant, exponent, surfaceScale, lightColor))
    addPointLight(filters.at(-1), x, y, z)
    filters.push(createCompositeLight('arithmetic', resultName, composite, 'spec-light-result'))
    return filters
}


const createDiffusePointLight = function(resultName, constant, surfaceScale, x, y, z, lightColor, inputName='SourceGraphic', composite='SourceGraphic') {
    let filters = []
    filters.push(createDiffuseLight(inputName, resultName, constant, surfaceScale, lightColor))
    addPointLight(filters.at(-1), x, y, z)
    filters.push(createCompositeLight('arithmetic', resultName, composite, 'spec-light-result'))
    return filters
}




// Composites turbulence with another input 
// to add variation to an element
// Had to use two layers of Blur to get the randomized pattern to not 
// have a very grid/dot like bias. Still unclear on exactly why that 
// happens, but it has to do with the turbulence frequencies and compositing.
const createTurbulenceComposite = function(freq, octaves, inputName='SourceGraphic', intensityVector=`0.63 0.81 0.75 2.5`, noiseType='turbulence') {
    let filters = []
    filters.push(createTurbulence('turbulence-composite-1', freq, octaves, noiseType))
    filters.push(createBlurElem('turbulence-composite-1', 0.03, 'blur-result-1'))
    filters.push(createColorMatrixAlpha('blur-result-1', 'turbulence-intensify', intensityVector))
    filters.push(createBlurElem('turbulence-intensify', 0.00, 'blur-result-2'))
    filters.push(createCompositeElem('in', inputName, 'blur-result-2', 'turbulence-composite-result'))
    return filters
}




class Drawer {

    constructor (
        plotElement,
        rootId
    ) {
        this.rootId = rootId
        this.plotElement = plotElement
        this.defsElement = plotElement.querySelector('defs')
        this.counter = 0
        this.gradientCounter = 0
        this.filterCounter = 0
    }


    addLabel(labelText, x=0, y=5, width=30) {
        let element = document.createElementNS(svgNameSpaceURI, `text`)
        element.setAttribute(`id`, labelText.slice(0, 30))
        element.setAttribute(`x`, x) 
        element.setAttribute(`y`, y) 
        element.classList.add(`label-text`)
        element.textContent = labelText
        this.plotElement.appendChild(element)
    }

    blurFilter(inputElements, blur=0.02, inputName='SourceGraphic') {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let filterElements = []
        let id = `blur-only-${this.filterCounter}`
        filter.setAttribute('id', id)

        let blurId = `blur-${this.filterCounter}`
        filterElements.push(createBlurElem(inputName, blur, blurId))

        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }

    erodeAndDistortFilter(inputElements, freq, octaves, erode=0.5, displacement=0.2, blur=0.02, inputName='SourceGraphic', noiseType='turbulence') {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `erode-distort-${this.filterCounter}`
        filter.setAttribute('id', id)

        let filterElements = []
        let erodeId = `erode-${this.filterCounter}`
        filterElements.push(createMorphologyElem(inputName, 'erode', erode, erodeId))

        filterElements = filterElements.concat(createTurbulenceDisplacement(freq, octaves, displacement, erodeId, noiseType))

        let blurId = `blur-${this.filterCounter}`
        filterElements.push(createBlurElem('turbulence-displace-result', blur, blurId))

        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }

    // shadowArgs - args for creating one or both of outer/inner 
    //              shadows
    createShadowFilters(shadowArgs, inputName='SourceGraphic') {
        let filters = []
        let mergeNode, blurId
        let merge = document.createElementNS(svgNameSpaceURI, 'feMerge')
        merge.setAttribute('result', 'shadows-result')

        // Grow the element and blur it
        if (shadowArgs.outer) {
            let dilateId = `dilate-${this.filterCounter}`
            filters.push(createMorphologyElem(inputName, 'dilate', shadowArgs.grow, dilateId))
            blurId = `blur-${this.filterCounter}`
            filters.push(createBlurElem(dilateId, shadowArgs.blurOuter, blurId))

            // Flood the whole area, but then cur to inside the grow/blur
            filters.push(createFloodElem(shadowArgs.floodColorOuter, shadowArgs.floodOpacityOuter, 'flood-1'))
            filters.push(createCompositeElem('in', 'flood-1', blurId, 'flood-shadow-1'))
            // Cut out original shape to get outer shadow
            filters.push(createCompositeElem('out', 'flood-shadow-1', inputName, 'outer-shadow'))

            mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'outer-shadow')
            merge.append(mergeNode)
            this.filterCounter++
        }

        if (shadowArgs.inner) {
            let erodeId = `erode-${this.filterCounter}`
            filters.push(createMorphologyElem(inputName, 'erode', shadowArgs.shrink, erodeId))
            blurId = `blur-${this.filterCounter}`
            filters.push(createBlurElem(erodeId, shadowArgs.blurInner, blurId))

            // Flood the whole area, take area outside shrink/blur
            filters.push(createFloodElem(shadowArgs.floodColorInner, shadowArgs.floodOpacityInner, 'flood-2'))
            filters.push(createCompositeElem('out', 'flood-2', blurId, 'flood-shadow-2'))
            // Cut inside original shape to get inner shadow
            filters.push(createCompositeElem('in', 'flood-shadow-2', inputName, 'inner-shadow'))

            mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'inner-shadow')
            merge.append(mergeNode)
            this.filterCounter++
        }

        // Optionally merge it with the input shape. 
        if (shadowArgs.composite) {
            mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'SourceGraphic')
            merge.append(mergeNode)
        }

        filters.push(merge)
        return filters
    }



    // Take the input and:
    // - adds displacement with turbulence
    // - blurs the results 
    // - composites the blur result with separate turbulence
    // - Adds shadows to give a glow to the displaced/blurred input. 
    // NOTES: could change the order to add shadows before blur, 
    // but I currently like the effect that doing it after achieved.  
    createTurbulenceBlurShadows(shadowArgs, inputElements, freq, freq2, octaves, stdDev, displacement, intensityVector, mergeDisplacement=false) {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-turbulent-shadows-${this.filterCounter}`
        filter.setAttribute('id', id)
        setFilterRange(filter)

        let filterElements = []
        filterElements = filterElements.concat(createTurbulenceDisplacement(freq, octaves, displacement, 'SourceGraphic'))
        filterElements.push(createBlurElem('turbulence-displace-result', stdDev, 'blur-displace'))
        filterElements = filterElements.concat(createTurbulenceComposite(freq2, octaves, 'blur-displace', intensityVector))

        filterElements = filterElements.concat(this.createShadowFilters(shadowArgs, 'turbulence-composite-result'))

        if (mergeDisplacement) {
            let merge = document.createElementNS(svgNameSpaceURI, 'feMerge')
            merge.setAttribute('result', 'merged-result')

            let mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'turbulence-composite-result')
            merge.append(mergeNode)

            mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'shadows-result')
            merge.append(mergeNode)

            filterElements.push(merge)
        }

        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }


    // Take the input and:
    // - adds shadows (inner/outer) according to config
    // - composites the original with turbulence.
    // - blurs the output 
    createBlurShadowComposite(inputElements, shadowArgs, freq, octaves, stdDev, intensityVector, over=true) {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-shadows-and-composite-blur-${this.filterCounter}`
        filter.setAttribute('id', id)


        let filterElements = []
        let merge = document.createElementNS(svgNameSpaceURI, 'feMerge')
        merge.setAttribute('result', 'blur-shadow-composite-result')
        // Using the plain Turbulence Composite, could shift to the double, but it would 
        // need some work. 
        filterElements = filterElements.concat(this.createShadowFilters(shadowArgs, 'SourceGraphic'))
        filterElements = filterElements.concat(createTurbulenceComposite(freq, octaves, 'SourceGraphic', intensityVector))
        // filterElements.push(createBlurElem('turbulence-composite-result', stdDev, 'blur-double-result'))

        if (over) {
            filterElements.push(createCompositeElem('over', 'shadows-result', 'turbulence-composite-result', 'shadows-over-1'))
        } else {
            let mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'shadows-result')
            merge.append(mergeNode)
            mergeNode =  document.createElementNS(svgNameSpaceURI, "feMergeNode")
            mergeNode.setAttribute('in', 'turbulence-composite-result')
            merge.append(mergeNode)
            filterElements.push(merge)
        }

        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }


    // Take a background as the input, this generates a pattern 
    //   on that background.
    // - The frequencies and color Matrix will both be extremely 
    //   important. 
    createTurbulencePattern(inputElements, freq, octaves, colorMatrix, noiseType='turbulence') {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-create-pattern-${this.filterCounter}`
        filter.setAttribute('id', id)

        let filterElements = []
        filterElements.push(createTurbulence('turbulence-pattern', freq, octaves, noiseType))
        filterElements.push(createFullColorMatrix('turbulence-pattern', 'pattern-creation-colorMat', colorMatrix))
        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }



    // Take the input and:
    // - blurs the input 
    // - composites the blur result with turbulence.
    createBlurComposite(inputElements, freq, octaves, stdDev, intensityVector) {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-blur-${this.filterCounter}`
        filter.setAttribute('id', id)

        let filterElements = []
        // Using the plain Turbulence Composite, could shift to the double, but it would 
        // need some work. 
        filterElements = filterElements.concat(createTurbulenceComposite(freq, octaves, 'SourceGraphic', intensityVector))
        filterElements.push(createBlurElem('double-turbulence-result', stdDev, 'blur-double-result'))
        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }


    // Take the input and:
    // - adds displacement with turbulence
    // - blurs the results 
    createTurbulenceBlur(inputElements, freq, octaves, stdDev, displacement=1) {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-turbulent-blur-${this.filterCounter}`
        filter.setAttribute('id', id)
        // Very useful if you are having filters hit hard edges; 
        // this expands the filter way beyond an edge
        setFilterRange(filter)

        let filterElements = []
        filterElements = filterElements.concat(createTurbulenceDisplacement(freq, octaves, displacement, 'SourceGraphic'))

        filterElements.push(createBlurElem('turbulence-displace-result', stdDev, 'blur-displace'))

        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }


    // Take the input and:
    // - adds displacement with turbulence
    // - blurs the results 
    // - composites the blur result with separate turbulence
    createTurbulenceBlurComposite(inputElements, freq, freq2, octaves, stdDev, displacement=1, intensityVector, noiseType='turbulence') {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-turbulence-${this.filterCounter}`
        filter.setAttribute('id', id)

        let filterElements = []
        filterElements = filterElements.concat(createTurbulenceDisplacement(freq, octaves, displacement, 'SourceGraphic'))

        filterElements.push(createBlurElem('turbulence-displace-result', stdDev, 'blur-displace'))

        filterElements = filterElements.concat(createTurbulenceComposite(freq2, octaves, 'blur-displace', intensityVector, noiseType))
        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }


    // Take the input and:
    // - adds displacement with turbulence
    // - blurs the results 
    // - composites the blur result with separate turbulence
    createTurbulenceBlurLightComposite(inputElements, freq, freq2, octaves, stdDev, displacement=1, intensityVector, noiseType='turbulence', constant, exponent, surfaceScale, x, y, z, lightColor='white') {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-turbulence-${this.filterCounter}`
        filter.setAttribute('id', id)
        setFilterRange(filter)

        let filterElements = []
        filterElements = filterElements.concat(createTurbulenceDisplacement(freq, octaves, displacement, 'SourceGraphic'))

        filterElements.push(createBlurElem('turbulence-displace-result', stdDev, 'blur-displace'))

        filterElements = filterElements.concat(createTurbulenceComposite(freq2, octaves, 'blur-displace', intensityVector, noiseType))

        // Only purpose is to be used with the specular light
        // It needs something solid to shine the light on
        filterElements.push(createFloodElem('black', 0.0, 'flood-test-light'))
        filterElements = filterElements.concat(createSpecularPointLight('lighting-result', constant, exponent, surfaceScale, lightColor, x, y, z, 'flood-test-light', 'turbulence-composite-result'))


        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
        return filter
    }


    // TODO: 
    // This is very different than the specular / point light. 
    // It seems to darken everything by default, could not get 
    // it to work with my input, just ended up turning most 
    // of it black
    createTurbulenceDiffuseLightComposite(inputElements, freq, freq2, octaves, stdDev, displacement=1, intensityVector, noiseType='turbulence', constant, surfaceScale, x, y, z, lightColor='white') {
        inputElements = ensureIsArray(inputElements)
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        let id = `filter-turb-light-diffuse-${this.filterCounter}`
        filter.setAttribute('id', id)
        setFilterRange(filter)

        let filterElements = []
        filterElements = filterElements.concat(createTurbulenceDisplacement(freq, octaves, displacement, 'SourceGraphic'))

        filterElements.push(createBlurElem('turbulence-displace-result', stdDev, 'blur-displace-12'))

        filterElements = filterElements.concat(createTurbulenceComposite(freq2, octaves, 'blur-displace-12', intensityVector, noiseType))

        filterElements = filterElements.concat(createDiffusePointLight('lighting-result', constant, surfaceScale, x, y, z, lightColor, 'blur-displace-12', 'turbulence-composite-result'))


        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))

        this.filterCounter++
    }


    // Adds a shadow filter
    // inputElements - Shapes (or group/element) the filter is applied to 
    // shadowArgs  - Arguments that configure exactly what the shadows will look like
    addShadowFilters(inputElements, shadowArgs, id=`filter-shadow`) {
        inputElements = ensureIsArray(inputElements)
        let filterElements = []
        let svgDefs = this.plotElement.querySelector('defs')
        let filter = document.createElementNS(svgNameSpaceURI, "filter")
        id = `${id}-${this.filterCounter}`
        filter.setAttribute('id', id)
        setFilterRange(filter)

        filterElements = filterElements.concat(this.createShadowFilters(shadowArgs))
        filterElements.forEach(filterElement => filter.append(filterElement))
        svgDefs.appendChild(filter)
        inputElements.forEach(shape => shape.setAttribute(`filter`, `url(#${id})`))
    }


    createLinearGradient(svgElement, color1, color2, startX, startY, endX, endY, offset1, offset2) { 
        let svgDefs = svgElement.querySelector('defs')
        let gradient = document.createElementNS(svgNameSpaceURI, "linearGradient")
        let id = `linear-gradient-${this.gradientCounter}`
        gradient.setAttribute('id', id)
        gradient.setAttribute('x1', startX)
        gradient.setAttribute('y1', startY)
        gradient.setAttribute('x2', endX)
        gradient.setAttribute('y2', endY)

        let stop1 = document.createElementNS(svgNameSpaceURI, 'stop')
        stop1.setAttribute('offset', offset1)
        stop1.setAttribute('stop-color', color1)
        gradient.append(stop1)

        // Reduce saturation and lightness to get the
        // end color that will create the shadow effect
        let stop2 = document.createElementNS(svgNameSpaceURI, 'stop')
        stop2.setAttribute('offset', offset2)
        stop2.setAttribute('stop-color', color2)
        gradient.append(stop2)

        // Add to the defs element
        svgDefs.appendChild(gradient)
        this.gradientCounter++
        return id
    }


    // Hue goes 0-360, so it gets its own method here
    randHue(hueBase, hueVary) {
        let rand = (Math.random() * 2 - 1) * hueVary
        let hue = hueBase + rand * 360
        hue = hue < 0 ? 360 + hue : hue % 360
        return roundDecimalPlaces(Math.max(0, hue), 1)
    }


    randSatLight(base, vary) {
        let rand = (Math.random() * 2 - 1) * vary
        return roundDecimalPlaces(Math.max(0, Math.min(100, base + (rand * 100))), 1)
    }


    getRandHsl(hslBase, varyHsl) {
        let hue = this.randHue(hslBase.getHue(), varyHsl.getHueVary())
        let sat = this.randSatLight(hslBase.getSaturation(), varyHsl.getSatVary())
        let light = this.randSatLight(hslBase.getLightness(), varyHsl.getLightVary())
        let hsl = `hsl(${hue}, ${sat}%, ${light}%, ${hslBase.getAlpha()})`
        return hsl
    }


    // Select the plot and group element. 
    // Depending on the config, they may be the same thing
    selectPlotElements(group, asDef, groups=1, id='') {
        let plotElements = []
        let groupElements = []
        if (group) {
            for (let i = 0; i < groups; i++) {
                let groupElement = document.createElementNS(svgNameSpaceURI, 'g')
                this.plotElement.append(groupElement)
                groupElement.setAttribute('id', `${id}`)
                groupElements.push(groupElement)
                plotElements.push(asDef ? this.defsElement : groupElement)
            }
        } else {
            let plotElement = asDef ? this.defsElement : this.plotElement
            groupElements.push(this.plotElement)
        }
        return [plotElements, groupElements]
    }


    drawPathGroups(baseId, pathGroupDs, stylingMaps, asDef, group=true, groups=1) {
        let n = 0
        let [plotElements, groupElements] = this.selectPlotElements(group, asDef, groups, baseId)
        let ids = []
        let elements = []
        pathGroupDs = ensureIsArray(pathGroupDs)
        for (let pathGroup of pathGroupDs) {
            let plotUse = groupElements[n % groupElements.length]
            let plotElement = plotElements[n % plotElements.length]
            let idx = n % stylingMaps.length
            ids.push([])
            let styling = stylingMaps[idx]
            let stylingDirect = asDef ? new Map() : styling
            for (let path of pathGroup) {
                let id = `${baseId}-${this.counter}`
                ids.at(-1).push(id)
                let rawElement = drawPath(
                    plotElement,
                    path,
                    stylingDirect,
                    id
                )
                if (asDef) {
                    let [useId, element] = addUse(plotUse, id, styling)
                    elements.push(element)
                } else {
                    elements.push(rawElement)
                }
                this.counter++
            }
            n++
        }
        return [groupElements, ids]
    }


    drawPathsWithGradients(baseId, pathDs, stylingMap, asDef, hsl, hslVary, offset1=0, offset2=0, offsetVary=0, group=true, groups=1) {
        let [plotElements, groupElements] = this.selectPlotElements(group, asDef, groups, baseId)
        // let plotUse = groupElement
        let ids = []
        pathDs = ensureIsArray(pathDs)
        let n = 0
        for (let path of pathDs) {
            let plotUse = groupElements[n % groupElements.length]
            let plotElement = plotElements[n % plotElements.length]
            let color1 = this.getRandHsl(hsl, hslVary)
            let color2 = this.getRandHsl(hsl, hslVary)
            let gradientId = this.createLinearGradient(this.plotElement, color1, color2, `0%`, `0%`, `100%`, `100%`, `0%`, `100%`)
            stylingMap.set(`fill`, `url(#${gradientId})`)
            let styling = asDef ? new Map() : stylingMap
            let id = `${baseId}-${this.counter}`
            ids.push(id)
            let rawElement = drawPath(
                plotElement,
                path,
                styling,
                id
            )
            if (asDef) {
                let [useId, element] = addUse(plotUse, id, stylingMap)
            }
            this.counter++
        }
        return [groupElements, ids]
    }


    drawPathsColorRand(baseId, pathDs, stylingMap, asDef, hsl, hslVary, cssName, group=true, className=null, randomize=false, groups=1) {
        let [plotElements, groupElements] = this.selectPlotElements(group, asDef, groups, baseId)
        if (className) {
            groupElements.forEach(group => group.setAttribute('class', className))
        }
        let ids = []
        let elements = []
        if (randomize) {
            pathDs = pathDs
                .map(value => ({ value, sort: Math.random() }))
                .sort((a, b) => a.sort - b.sort)
                .map(({ value }) => value)
        }
        let n = 0
        for (let path of pathDs) {
            let plotUse = groupElements[n % groupElements.length]
            let plotElement = plotElements[n % plotElements.length]
            let hslRand = this.getRandHsl(hsl, hslVary)
            stylingMap.set(cssName, hslRand)
            let styling = asDef ? new Map() : stylingMap
            let id = `${baseId}-${this.counter}`
            ids.push(id)
            let rawElement = drawPath(
                plotElement,
                path,
                styling,
                id
            )
            if (asDef) {
                let [useId, element] = addUse(plotUse, id, stylingMap)
                elements.push(element)
            } else {
                elements.push(rawElement)
            }

            n++
            this.counter++
        }
        return [groupElements, ids]
    }


    // Draw randomized colors, but goes from start point to an end point
    // with a little variation in between
    drawPathsColorSequenceRand(baseId, pathDs, stylingMap, asDef, hsl, hslVary, subVary, cssName, group=true, randomize=false, groups=1) {
        let [plotElements, groupElements] = this.selectPlotElements(group, asDef, groups, baseId)
        let ids = []
        let elements = []
        if (randomize) {
            pathDs = pathDs
                .map(value => ({ value, sort: Math.random() }))
                .sort((a, b) => a.sort - b.sort)
                .map(({ value }) => value)
        }
        let n = 0
        let hueStep = hslVary.getHueVary() * 360 / pathDs.length
        let satStep = hslVary.getSatVary() * 100  / pathDs.length
        let lightStep = hslVary.getLightVary() * 100 / pathDs.length
        let alphaStep = hslVary.getAlphaVary() * 1 / pathDs.length
        hslVary.modulateVary(subVary)
        for (let path of pathDs) {
            let plotUse = groupElements[n % groupElements.length]
            let plotElement = plotElements[n % plotElements.length]
            let hslRand = this.getRandHsl(hsl, hslVary)
            stylingMap.set(cssName, hslRand)
            let styling = asDef ? new Map() : stylingMap
            let id = `${baseId}-${this.counter}`
            ids.push(id)
            let rawElement = drawPath(
                plotElement,
                path,
                styling,
                id
            )
            if (asDef) {
                let [useId, element] = addUse(plotUse, id, stylingMap)
                elements.push(element)
            } else {
                elements.push(rawElement)
            }

            hsl.changeHue(hueStep).changeSaturation(satStep).changeLightness(lightStep).changeAlpha(alphaStep)
            n++
            this.counter++
        }
        return [groupElements, ids]
    }




    drawPaths(baseId, pathDs, stylingMap, asDef, group=true, groups=1) {
        let [plotElements, groupElements] = this.selectPlotElements(group, asDef, groups, baseId)
        let styling = asDef ? new Map() : stylingMap
        let ids = []
        let elements = []
        pathDs = ensureIsArray(pathDs)
        let n = 0
        for (let path of pathDs) {
            let plotUse = groupElements[n % groupElements.length]
            let plotElement = plotElements[n % plotElements.length]
            let id = `${baseId}-${this.counter}`
            ids.push(id)
            let rawElement = drawPath(
                plotElement,
                path,
                styling,
                id
            )
            if (asDef) {
                let [useId, element] = addUse(plotUse, id, stylingMap)
                elements.push(element)
            } else {
                elements.push(rawElement)
            }

            n++
            this.counter++
        }
        return [groupElements, ids]
    }


    drawCircles(baseId, circles, stylingMap, asDef, groupElement=null) {
        let plotElement = asDef ? this.defsElement : this.plotElement
        let stylingDirect = asDef ? new Map() : stylingMap
        circles = ensureIsArray(circles)
        let ids = []
        let elements = []
        for (let circle of circles) {
            let id = `${baseId}-${this.counter}`
            ids.push(id)
            let drawnCircle = drawCircle(
                plotElement,
                circle.cx,
                circle.cy,
                circle.r,
                stylingDirect,
                id
            )
            let element = drawnCircle
            if (asDef) {
                let useElement = groupElement === null ? this.plotElement : groupElement
                let [idRslt, elementRslt] = addUse(useElement, id, stylingMap)
                element = elementRslt
            }
            elements.push(element)
            this.counter++
        }
        return [elements, ids]
    }


    // I use this to move things around when needed
    appendToElement(elements) {
        elements.forEach(element => this.plotElement.appendChild(element))
    }
}


export { drawPath, drawCircle, addUse, Drawer, transformScale, transformScaleSkewY }
